/**
 * Shared auth form validation - email + password rules.
 *
 * Ported from the unified app's `src/auth/validation.ts` (which itself mirrors
 * AISquare-Studio-FE-React) so the auth slot atoms (`PasswordRequirements`,
 * `AuthFieldRow`) and the consuming screens share ONE rule set instead of each
 * re-deriving it. Pure data + pure functions - no React, no library import - so
 * it is SSR-safe and tree-shakes cleanly.
 *
 *   import { validEmail, passwordChecks, PASSWORD_REQUIREMENTS } from "@aisquare/ui";
 */

/**
 * Upper bound on password length. The reference app caps it because the RSA
 * password-encryption step has a maximum plaintext size; mirrored here so a
 * consumer's "too long" rule matches the backend. Override at the call site if
 * your backend differs - these helpers accept the value implicitly via the
 * exported constant.
 */
export const MAX_PASSWORD_LENGTH = 128;

const SPECIAL = /[!@#$%^&*(),.?":{}|<>]/;

/** True for a syntactically plausible email. Mirrors the unified app's check. */
export const validEmail = (email: string): boolean =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

/** Per-rule booleans, for the live requirement checklist. */
export interface PasswordCheckResult {
  length: boolean;
  uppercase: boolean;
  number: boolean;
  special: boolean;
}

export function passwordChecks(password: string): PasswordCheckResult {
  return {
    length: password.length >= 8 && password.length <= MAX_PASSWORD_LENGTH,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: SPECIAL.test(password),
  };
}

export type PasswordCheck = keyof PasswordCheckResult;

export interface PasswordRequirement {
  key: PasswordCheck;
  label: string;
}

export const PASSWORD_REQUIREMENTS: readonly PasswordRequirement[] = [
  { key: "length", label: "At least 8 characters" },
  { key: "uppercase", label: "One uppercase letter" },
  { key: "number", label: "One number" },
  { key: "special", label: "One special character" },
];

export const PASSWORD_HINT =
  "At least 8 characters, with an uppercase letter, a number, and a special character.";

/** First failing rule as a human-readable message, or `null` when valid. */
export function passwordProblem(password: string): string | null {
  if (password.length < 8) return "Password must be at least 8 characters.";
  if (password.length > MAX_PASSWORD_LENGTH)
    return `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer.`;
  if (!/[A-Z]/.test(password)) return "Password must contain at least 1 uppercase letter.";
  if (!/[0-9]/.test(password)) return "Password must contain at least 1 number.";
  if (!SPECIAL.test(password)) return "Password must contain at least 1 special character.";
  return null;
}
